import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Compass, Users, Leaf, Map, Check, Sparkles, Award, Truck, Factory, Instagram, Facebook, Play, ArrowRight, Mail, MapPin, Phone, Copy, Volume2, VolumeX } from "lucide-react";

const PRODUCT_FRONT_IMG = "/images/product-front.png";
const PRODUCT_BACK_IMG = "/images/product-back.png";
const MAKHANA_BOWL_IMG = "/images/makhana-bowl.png";
const MACRO_POUCH_IMG = "/images/container-pouch.png";
const NUTRITION_POUCH_IMG = "/images/nutrition-pouch.png";
const PREMIUM_MAKHANA_VIDEO = "/videos/Premuim-Makhana.mp4";
const FARMER_HARVESTING_VIDEO = "/videos/Farmer-Harvesting-Makhana.mp4";
const UNIFORM_MAKHANA_VIDEO = "/videos/Uniform-Makahana.mp4";
const MAKHANA_KIDS_VIDEO = "/videos/Makhana-Kids-Video.mp4";
const MAKHANA_OFFICE_VIDEO = "/videos/Makhana-Office-Video.mp4";
const MAKHANA_HEALTH_VIDEO = "/videos/Makhana-Health-Video.mp4";
const MAKHANA_OLDAGE_VIDEO = "/videos/Makhana-OldAge-Video.mp4";
const MAKHANA_JOURNEY_1 = "/images/Makahan-Journey1.jpeg";
const MAKHANA_JOURNEY_2 = "/images/Makahan-Journey2.jpeg";
const MAKHANA_JOURNEY_3 = "/images/Makahan-Journey3.jpeg";
const PRODUCT_EXPORT_FRONT = "/images/ProductType-Export-Front.jpeg";
const PRODUCT_EXPORT_BACK = "/images/ProductType-Export-Back.jpeg";
const PRODUCT_PILLOW_SMALL = "/images/ProductType-Pillow-Pouch-Small.jpeg";
const PRODUCT_PILLOW_MEDIUM = "/images/ProductType-Pillow-Pouch-Medium.jpeg";
const PRODUCT_TRANSPARENT = "/images/ProductType-TranspareParent-Pouch.jpeg";

const MARKET_PROBLEMS = [
    {
        id: "mixed-quality",
        icon: Sparkles,
        title: "Mixed Quality",
        desc: "Inconsistent grading and poor sorting compromise the premium experience"
    },
    {
        id: "inconsistent-sizing",
        icon: Award,
        title: "Inconsistent Sizing",
        desc: "Non-uniform makhana affects cooking quality and presentation"
    },
    {
        id: "moisture-freshness",
        icon: Leaf,
        title: "Moisture & Freshness Loss",
        desc: "Poor packaging leads to sogginess and loss of natural crunch"
    },
    {
        id: "premium-pricing",
        icon: Check,
        title: "Premium Pricing Without Standards",
        desc: "High prices without corresponding quality assurance and consistency"
    }
];

const QUALITY_3C = [
    {
        id: "color",
        number: "01",
        title: "Color",
        desc: "Bright white makhana reflects freshness and premium quality. Every batch is carefully inspected for appearance and purity."
    },
    {
        id: "crunch",
        number: "02",
        title: "Crunch",
        desc: "Makhana naturally absorbs moisture. Our moisture-controlled handling and packaging preserve crunch and freshness."
    },
    {
        id: "consistency",
        number: "03",
        title: "Consistency",
        desc: "We carefully sort and grade makhana into uniform premium categories like 4+ Suta, 5+ Suta, and 6+ Suta."
    }
];

export default function Home() {
    const [email, setEmail] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [mutedVideos, setMutedVideos] = useState({
        hero: true,
        bihar: true,
        sourcing: true,
        kids: true,
        office: true,
        health: true,
        oldage: true
    });
    const [currentJourneyImage, setCurrentJourneyImage] = useState(0);
    const journeyImages = [MAKHANA_JOURNEY_1, MAKHANA_JOURNEY_2, MAKHANA_JOURNEY_3];

    const handleSubmit = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setIsSubmitted(true);
            setTimeout(() => setIsSubmitted(false), 3000);
        }
    };

    const toggleMute = (videoKey) => {
        setMutedVideos(prev => ({
            ...prev,
            [videoKey]: !prev[videoKey]
        }));
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentJourneyImage(prev => (prev + 1) % journeyImages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen text-white">
            {/* THE REVEAL - HERO SECTION */}
            <section id="reveal" className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/80 z-10" />
                
                <motion.div 
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="relative z-20 text-center max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
                >
                    <div>
                        <motion.h1 
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="text-4xl md:text-6xl lg:text-8xl font-black tracking-tighter mb-6 leading-[0.85]"
                        >
                            <span className="bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent">
                                Premium Makhana.
                            </span>
                            <br />
                            <span className="text-white">From Bihar Farms to Modern Homes.</span>
                        </motion.h1>
                        
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="text-lg md:text-xl text-gray-300 mb-12 leading-relaxed max-w-2xl"
                        >
                            Born in Bihar — the birthplace of makhana — we bring carefully sourced premium makhana directly from trusted farms to modern homes with uncompromised quality, purity, and freshness.
                        </motion.p>
                    </div>
                    
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 100 }}
                        animate={{
                            opacity: 1, 
                            scale: 1,
                            y: [0, -8, 0, 4, 0],
                            rotateY: [0, 2, 0, -2, 0]
                        }}
                        whileInView={{
                            y: [0, -15, 0],
                            rotateY: [0, 3, 0, -3, 0]
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: [0.4, 0.0, 0.2, 1],
                            scale: { duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] },
                            opacity: { duration: 1.2, delay: 0.4 }
                        }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 via-yellow-500/10 to-amber-500/15 rounded-3xl blur-2xl" />
                        <motion.div
                            animate={{
                                scale: [1, 1.05, 1],
                                opacity: [0.3, 0.6, 0.3]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-yellow-400/20 rounded-3xl blur-xl"
                        />
                        <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl" />
                            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-800/50">
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={currentJourneyImage}
                                        src={journeyImages[currentJourneyImage]}
                                        alt="Makhana Journey"
                                        initial={{ opacity: 0, scale: 1.1 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.8, ease: "easeInOut" }}
                                        className="w-full h-full object-cover absolute inset-0"
                                    />
                                </AnimatePresence>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                
                                {/* Image Indicators */}
                                <div className="absolute bottom-20 left-6 right-6 flex justify-center gap-2">
                                    {journeyImages.map((_, index) => (
                                        <motion.div
                                            key={index}
                                            onClick={() => setCurrentJourneyImage(index)}
                                            whileHover={{ scale: 1.2 }}
                                            whileTap={{ scale: 0.8 }}
                                            className={`h-2 rounded-full cursor-pointer transition-all ${
                                                index === currentJourneyImage 
                                                    ? 'w-8 bg-amber-400' 
                                                    : 'w-2 bg-white/40'
                                            }`}
                                        />
                                    ))}
                                </div>
                                
                                <div className="absolute bottom-6 left-6 right-6">
                                    <div className="text-amber-400 text-sm font-semibold mb-2">Premium Quality</div>
                                    <div className="text-white text-xl font-bold">From Bihar Farms to Modern Homes</div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </section>

            {/* WHO WE ARE SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* Left - Text Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                            >
                                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400" />
                                <span className="text-sm font-medium tracking-wide uppercase text-white">Our Story</span>
                            </motion.div>
                            
                            <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-8 leading-tight">
                                <span className="relative">
                                    Rooted in Bihar.
                                    <motion.span
                                        className="absolute inset-0 bg-gradient-to-r from-amber-500/30 via-yellow-500/20 to-amber-500/30 blur-2xl -z-10"
                                        animate={{
                                            opacity: [0.3, 0.6, 0.3]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    />
                                </span>
                                <br />
                                <span className="text-white">Built on Trust.</span>
                            </h2>
                            
                            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
                                <p>
                                    We are a Bihar-rooted brand built by people who understand makhana quality at its source.
                                </p>
                                <p>
                                    After seeing inconsistent quality, mixed grading, and overpriced products in the market, we set out to create a brand focused on purity, consistency, and honest premium quality.
                                </p>
                                <p className="text-white font-semibold">
                                    Our mission is simple: deliver authentic premium makhana without compromise.
                                </p>
                            </div>
                        </motion.div>
                        
                        {/* Right - Premium Image/Video */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl" />
                                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-800/50">
                                    <video 
                                        src={FARMER_HARVESTING_VIDEO} 
                                        autoPlay
                                        loop
                                        muted={mutedVideos.bihar}
                                        playsInline
                                        controls={false}
                                        alt="Farmer Harvesting Makhana in Bihar" 
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    
                                    {/* Sound Toggle Button */}
                                    <motion.button
                                        onClick={() => toggleMute('bihar')}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="absolute top-4 right-4 bg-black/50 backdrop-blur-md rounded-full p-3 border border-white/20 hover:bg-black/70 transition-colors"
                                    >
                                        {mutedVideos.bihar ? (
                                            <VolumeX size={18} className="text-white" />
                                        ) : (
                                            <Volume2 size={18} className="text-white" />
                                        )}
                                    </motion.button>
                                    
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <div className="text-amber-400 text-sm font-semibold mb-2">Authentic Bihar Origin</div>
                                        <div className="text-white text-xl font-bold">Sourced from the Birthplace of Makhana</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* WHAT WE ARE SOLVING SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Market Problems</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-8">
                            Fixing What the <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">Market Ignored</span>
                        </h2>
                    </motion.div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {MARKET_PROBLEMS.map((problem, index) => (
                            <motion.div
                                key={problem.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 + index * 0.1, duration: 0.6 }}
                                whileHover={{ 
                                    y: -8,
                                    boxShadow: "0 20px 40px rgba(251, 191, 36, 0.15)"
                                }}
                                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-amber-500/30 transition-all group"
                            >
                                <motion.div
                                    whileHover={{ rotate: 360, scale: 1.1 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4"
                                >
                                    <problem.icon size={20} className="text-white" />
                                </motion.div>
                                <h3 className="text-lg font-bold mb-2 text-white">{problem.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">{problem.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3C QUALITY STANDARD SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Quality Standard</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">
                            The <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">3C Quality Standard</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            "Premium makhana is defined by color, crunch, and consistency."
                        </p>
                    </motion.div>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                        {QUALITY_3C.map((quality, index) => (
                            <motion.div
                                key={quality.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 + index * 0.15, duration: 0.6 }}
                                whileHover={{ 
                                    y: -8,
                                    boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                                }}
                                className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-8 border border-white/10 backdrop-blur-2xl group"
                            >
                                <motion.div
                                    animate={{
                                        opacity: [0.3, 0.6, 0.3],
                                        scale: [1, 1.05, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: index * 0.5
                                    }}
                                    className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                />
                                <div className="relative">
                                    <div className="text-6xl font-black bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent mb-4">
                                        {quality.number}
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-white">{quality.title}</h3>
                                    <p className="text-gray-300 leading-relaxed">{quality.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PACKAGING SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <Factory size={16} className="text-amber-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Packaging</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-8">
                            Packed for <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">Freshness</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
                            Our makhana is processed and packed at our Faridabad facility using modern food-safe practices and moisture-protected packaging systems.
                        </p>
                    </motion.div>
                    
                    <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                        {/* Premium Pouch Display */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
                            whileHover={{ 
                                scale: 1.02,
                                boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                            }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl" />
                                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
                                    <img 
                                        src={MACRO_POUCH_IMG} 
                                        alt="Premium Packaging" 
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold backdrop-blur-md">
                                        FRESHNESS LOCKED
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        
                        {/* Packaging Benefits */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                            className="flex flex-col justify-center"
                        >
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8">
                                <h3 className="text-2xl font-bold mb-6 text-white">Every pack is designed to preserve:</h3>
                                <div className="space-y-6">
                                    {[
                                        { icon: Sparkles, title: "Crunch", desc: "Moisture-controlled packaging maintains perfect texture" },
                                        { icon: Leaf, title: "Nutrition", desc: "Food-safe processing preserves natural nutrients" },
                                        { icon: Award, title: "Texture", desc: "Premium quality from farm to home" },
                                        { icon: Check, title: "Shelf Life", desc: "Extended freshness without preservatives" }
                                    ].map((benefit, index) => (
                                        <motion.div
                                            key={benefit.title}
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                                            className="flex items-start gap-4"
                                        >
                                            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <benefit.icon size={20} className="text-white" />
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-bold text-white mb-1">{benefit.title}</h4>
                                                <p className="text-gray-400 text-sm">{benefit.desc}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FOLLOW THE JOURNEY - INSTAGRAM SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <Instagram size={16} className="text-amber-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">From Our Community</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">
                            Inside <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">Globic India</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            Authentic taste. Premium quality. Real makhana moments.
                        </p>
                    </motion.div>
                    
                    {/* Instagram Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        {[
                            { video: MAKHANA_KIDS_VIDEO, key: 'kids', title: "Kids Love Makhana", desc: "Healthy snacking for little ones" },
                            { video: MAKHANA_OFFICE_VIDEO, key: 'office', title: "Office Snacking", desc: "Perfect for work breaks" },
                            { video: MAKHANA_HEALTH_VIDEO, key: 'health', title: "Health Benefits", desc: "Nutritious and delicious" },
                            { video: MAKHANA_OLDAGE_VIDEO, key: 'oldage', title: "For All Ages", desc: "From kids to grandparents" }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 + index * 0.1, duration: 0.6 }}
                                whileHover={{ 
                                    y: -8,
                                    boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                                }}
                                className="relative group cursor-pointer"
                            >
                                <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-4 border border-white/10 backdrop-blur-2xl overflow-hidden">
                                    <motion.div
                                        animate={{
                                            opacity: [0.3, 0.6, 0.3],
                                            scale: [1, 1.05, 1]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: index * 0.5
                                        }}
                                        className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                    />
                                    <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-gray-800/50">
                                        <video 
                                            src={item.video}
                                            autoPlay
                                            loop
                                            muted={mutedVideos[item.key]}
                                            playsInline
                                            controls={false}
                                            alt={item.title} 
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                        
                                        {/* Sound Toggle Button */}
                                        <motion.button
                                            onClick={() => toggleMute(item.key)}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            className="absolute top-4 right-4 bg-black/50 backdrop-blur-md rounded-full p-2 border border-white/20 hover:bg-black/70 transition-colors"
                                        >
                                            {mutedVideos[item.key] ? (
                                                <VolumeX size={14} className="text-white" />
                                            ) : (
                                                <Volume2 size={14} className="text-white" />
                                            )}
                                        </motion.button>
                                        
                                        {/* Instagram icon */}
                                        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md rounded-full p-2">
                                            <Instagram size={16} className="text-white" />
                                        </div>
                                        
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <h4 className="text-white font-bold text-lg mb-1">{item.title}</h4>
                                            <p className="text-gray-300 text-sm">{item.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    
                    {/* CTA Button */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.a
                            href="https://www.instagram.com/globic.india"
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ 
                                scale: 1.05,
                                boxShadow: "0 0 50px rgba(251, 191, 36, 0.4)"
                            }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 backdrop-blur-md border border-amber-500/30 text-white rounded-full px-10 py-5 font-bold text-lg shadow-2xl hover:shadow-amber-500/30 transition-all"
                        >
                            <Instagram size={24} />
                            View on Instagram
                            <ArrowRight size={20} />
                        </motion.a>
                    </motion.div>
                </div>
            </section>

            {/* HOW WE SOURCE SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* Left - Text Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                            >
                                <Map size={16} className="text-amber-400" />
                                <span className="text-sm font-medium tracking-wide uppercase text-white">Sourcing</span>
                            </motion.div>
                            
                            <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-8 leading-tight">
                                Directly Sourced <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">from Bihar</span>
                            </h2>
                            
                            <div className="space-y-6 text-lg text-gray-300 leading-relaxed mb-8">
                                <p>
                                    We work closely with farmers and sourcing partners across Bihar to procure carefully cultivated premium raw makhana.
                                </p>
                                <p>
                                    Every procurement batch goes through strict inspection standards focused on:
                                </p>
                            </div>
                            
                            <div className="space-y-4">
                                {[
                                    "Whiteness",
                                    "Uniform Sizing",
                                    "Texture",
                                    "Freshness",
                                    "Premium Appearance"
                                ].map((item, index) => (
                                    <motion.div
                                        key={item}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400" />
                                        <span className="text-white font-medium">{item}</span>
                                    </motion.div>
                                ))}
                            </div>
                            
                            <p className="text-lg text-gray-300 leading-relaxed mt-8">
                                The sourcing process is continuously monitored to maintain consistency and trust.
                            </p>
                        </motion.div>
                        
                        {/* Right - Premium Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl" />
                                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-800/50">
                                    <video 
                                        src={UNIFORM_MAKHANA_VIDEO} 
                                        autoPlay
                                        loop
                                        muted={mutedVideos.sourcing}
                                        playsInline
                                        controls={false}
                                        alt="Uniform Makhana from Bihar" 
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                    
                                    {/* Sound Toggle Button */}
                                    <motion.button
                                        onClick={() => toggleMute('sourcing')}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="absolute top-4 right-4 bg-black/50 backdrop-blur-md rounded-full p-3 border border-white/20 hover:bg-black/70 transition-colors"
                                    >
                                        {mutedVideos.sourcing ? (
                                            <VolumeX size={18} className="text-white" />
                                        ) : (
                                            <Volume2 size={18} className="text-white" />
                                        )}
                                    </motion.button>
                                    
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <div className="text-amber-400 text-sm font-semibold mb-2">Direct from Bihar</div>
                                        <div className="text-white text-xl font-bold">Trusted Farm Partnerships</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* BUSINESS / EXPORT SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <Truck size={16} className="text-amber-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Business</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-8">
                            Serving Homes, Retail & <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">Global Markets</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
                            We supply premium makhana for diverse markets with the same commitment to quality.
                        </p>
                    </motion.div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        {[
                            "Retail Customers",
                            "Supermarkets",
                            "B2B Wholesale",
                            "Private Labeling",
                            "Export Markets"
                        ].slice(0, 4).map((market, index) => (
                            <motion.div
                                key={market}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 + index * 0.1, duration: 0.6 }}
                                whileHover={{ 
                                    y: -8,
                                    boxShadow: "0 20px 40px rgba(251, 191, 36, 0.15)"
                                }}
                                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-amber-500/30 transition-all text-center"
                            >
                                <div className="text-amber-400 text-2xl mb-3">
                                    <Users size={32} className="mx-auto" />
                                </div>
                                <h3 className="text-lg font-bold text-white">{market}</h3>
                            </motion.div>
                        ))}
                    </div>
                    
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 backdrop-blur-md border border-amber-500/20 rounded-3xl p-8">
                            <p className="text-2xl text-white font-semibold mb-4">
                                Whether serving homes or businesses, our focus remains the same:
                            </p>
                            <p className="text-3xl font-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">
                                Honest quality with premium consistency.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CONTACT SECTION */}
            <section className="relative py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                            className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8"
                        >
                            <Mail size={16} className="text-amber-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Contact</span>
                        </motion.div>
                        
                        <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">
                            Connect <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">With Us</span>
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            We're always available for retail, wholesale, export, and partnership inquiries.
                        </p>
                    </motion.div>
                    
                    {/* Contact Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-[0.85fr_1.3fr_0.85fr] gap-6 md:gap-8 items-stretch">
                        {/* Email Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1, duration: 0.6 }}
                            whileHover={{ 
                                y: -8,
                                boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                            }}
                            className="relative group h-full"
                        >
                            <div className="relative h-full min-h-[240px] flex flex-col bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <motion.div
                                    animate={{
                                        opacity: [0.3, 0.6, 0.3],
                                        scale: [1, 1.05, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                    className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                />
                                <div className="relative">
                                    <motion.div
                                        whileHover={{ rotate: 360, scale: 1.1 }}
                                        transition={{ duration: 0.5 }}
                                        className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6"
                                    >
                                        <Mail size={32} className="text-white" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-white mb-4">Mail Address</h3>
                                    <a 
                                        href="mailto:info@globicindia.com"
                                        className="text-amber-400 text-lg font-medium hover:text-amber-300 transition-colors mb-4 block"
                                    >
                                        info@globicindia.com
                                    </a>
                                    <motion.button
                                        onClick={() => navigator.clipboard.writeText('info@globicindia.com')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                                    >
                                        <Copy size={16} />
                                        Copy email
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                        
                        {/* Office Location Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            whileHover={{ 
                                y: -8,
                                boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                            }}
                            className="relative group h-full"
                        >
                            <div className="relative h-full min-h-[240px] flex flex-col bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <motion.div
                                    animate={{
                                        opacity: [0.3, 0.6, 0.3],
                                        scale: [1, 1.05, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: 0.5
                                    }}
                                    className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                />
                                <div className="relative">
                                    <motion.div
                                        whileHover={{ rotate: 360, scale: 1.1 }}
                                        transition={{ duration: 0.5 }}
                                        className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6"
                                    >
                                        <MapPin size={32} className="text-white" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-white mb-4">Office Location</h3>
                                    <a 
                                        href="https://www.google.com/maps/search/?api=1&query=Shop+No.+4+Ground+Floor+Habitat+Conscient+Sector+78+Faridabad+Town+Faridabad+Haryana+121002"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-300 text-base leading-relaxed mb-4 block hover:text-amber-400 transition-colors"
                                    >
                                        Shop No. 4, Ground Floor, Habitat Conscient,
                                        Sector 78, Faridabad, Haryana - 121002
                                    </a>
                                    <motion.button
                                        onClick={() => window.open('https://share.google/9Kt4WEKm4zWz3u6St', '_blank', 'noopener,noreferrer')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                                    >
                                        <MapPin size={16} />
                                        Open in Maps
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                        
                        {/* Phone Number Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                            whileHover={{ 
                                y: -8,
                                boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                            }}
                            className="relative group h-full"
                        >
                            <div className="relative h-full min-h-[240px] flex flex-col bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl">
                                <motion.div
                                    animate={{
                                        opacity: [0.3, 0.6, 0.3],
                                        scale: [1, 1.05, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: 1
                                    }}
                                    className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                />
                                <div className="relative">
                                    <motion.div
                                        whileHover={{ rotate: 360, scale: 1.1 }}
                                        transition={{ duration: 0.5 }}
                                        className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6"
                                    >
                                        <Phone size={32} className="text-white" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-white mb-4">Phone Number</h3>
                                    <a 
                                        href="tel:+919289199400"
                                        className="text-amber-400 text-2xl font-bold hover:text-amber-300 transition-colors mb-4 block"
                                    >
                                        +91 92891 99400
                                    </a>
                                    <motion.button
                                        onClick={() => navigator.clipboard.writeText('+919289199400')}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                                    >
                                        <Copy size={16} />
                                        Copy number
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="relative py-16 px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto flex items-center justify-center gap-6"
                >
                    <motion.a
                        href="https://www.instagram.com/globic.india"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-md transition-colors hover:border-amber-400/40 hover:bg-white/10"
                    >
                        <Instagram size={22} />
                    </motion.a>

                    <motion.a
                        href="https://www.facebook.com/profile.php?id=61572053011712"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-md transition-colors hover:border-amber-400/40 hover:bg-white/10"
                    >
                        <Facebook size={22} />
                    </motion.a>
                </motion.div>
            </section>
        </div>
    );
}
